import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import pb from '../../lib/pocketbase';
import { FaTimes } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faImages, faTrash, faFilePdf, faLink, faEnvelope, faShareAlt, faEuroSign, faMapMarkedAlt, faInfoCircle, faCheckCircle, faFolderOpen, faUsers, faUserTie, faTasks } from '@fortawesome/free-solid-svg-icons';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import styles from './ProjectForm.module.css'; // Reusing established styles
import AddressAutocomplete from './AddressAutocomplete';
import * as pdfjsLib from 'pdfjs-dist';

// Define the worker script path
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Fix for default Leaflet icons in React
if (L.Icon?.Default) {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
}

function ChantierForm({ chantier, onSave, onCancel }) {
    const [formData, setFormData] = useState({
        name: '',
        client: '',
        status: 'PLANNING',
        address: '',
        start_date: '',
        end_date_estimated: '',
        budget_total: '',
        budget_deposit: '',
        budget_paid: '',
        description: '',
        progress: 0,
        team: '',
        chef_chantier: [],
        workers: [],
        budget_hours: '',
        lat: null,
        lon: null
    });

    const [customers, setCustomers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [activeTab, setActiveTab] = useState('general'); // 'general', 'team', 'finance', 'media', 'docs'
    const [mapCenter, setMapCenter] = useState([43.9483, 4.8089]); // Avignon default
    const [showMap, setShowMap] = useState(false);

    // File Management
    const [filesBefore, setFilesBefore] = useState([]);
    const [filesAfter, setFilesAfter] = useState([]);
    const [filesPlans, setFilesPlans] = useState([]);
    const [previewBefore, setPreviewBefore] = useState([]);
    const [previewAfter, setPreviewAfter] = useState([]);
    const [previewPlans, setPreviewPlans] = useState([]);

    const [showQuickAddClient, setShowQuickAddClient] = useState(false);
    const [quickClientData, setQuickClientData] = useState({ name: '', email: '', phone: '', password: '' });
    const [isCreatingClient, setIsCreatingClient] = useState(false);
    const [isConvertingPdf, setIsConvertingPdf] = useState(false);

    const generatePassword = () => {
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
        let retVal = "";
        for (let i = 0, n = charset.length; i < 10; ++i) {
            retVal += charset.charAt(Math.floor(Math.random() * n));
        }
        setQuickClientData(prev => ({ ...prev, password: retVal + "!" }));
    };

    const handleQuickClientSubmit = async (e) => {
        e.preventDefault();
        if (!quickClientData.name || !quickClientData.email) {
            toast.error("Nom et Email requis");
            return;
        }
        setIsCreatingClient(true);
        try {
            const data = {
                name: quickClientData.name,
                email: quickClientData.email,
                phone: quickClientData.phone,
                password: quickClientData.password || (Math.random().toString(36).slice(-8) + "!"),
                passwordConfirm: quickClientData.password || (Math.random().toString(36).slice(-8) + "!"),
                type: 'PARTICULIER'
            };
            const record = await pb.collection('customers').create(data);
            toast.success("Client créé avec succès !");

            // Refresh customers list and select new one
            await loadCustomers();
            setFormData(prev => ({ ...prev, client: record.id }));

            setShowQuickAddClient(false);
            setQuickClientData({ name: '', email: '', phone: '', password: '' });
        } catch (err) {
            toast.error("Erreur création client: " + err.message);
        } finally {
            setIsCreatingClient(false);
        }
    };

    const beforeInputRef = useRef(null);
    const afterInputRef = useRef(null);
    const plansInputRef = useRef(null);

    useEffect(() => {
        loadCustomers();
        loadUsers();
    }, []);

    const loadUsers = async () => {
        try {
            // Fetch users with roles appropriate for staff/workers
            const records = await pb.collection('users').getFullList({
                sort: 'name',
                filter: 'role != "client"' // We don't want clients in the team
            });
            setAllUsers(records);
        } catch (err) {
            console.error("Error loading users", err);
        }
    };

    useEffect(() => {
        if (chantier) {
            setFormData({
                name: chantier.name || '',
                client: chantier.client || '',
                status: chantier.status || 'PLANNING',
                address: chantier.address || '',
                start_date: chantier.start_date ? chantier.start_date.split(' ')[0] : '',
                end_date_estimated: chantier.end_date_estimated ? chantier.end_date_estimated.split(' ')[0] : '',
                budget_total: chantier.budget_total || '',
                budget_deposit: chantier.budget_deposit || '',
                budget_paid: chantier.budget_paid || '',
                description: chantier.description || '',
                progress: chantier.progress || 0,
                team: chantier.team || '',
                chef_chantier: chantier.chef_chantier || [],
                workers: chantier.workers || [],
                budget_hours: chantier.budget_hours || '',
                lat: chantier.lat || null,
                lon: chantier.lon || null
            });

            // Load existing photo URLs for preview
            if (chantier.photos_before) {
                const urls = chantier.photos_before.map(f => pb.files.getURL(chantier, f));
                setPreviewBefore(urls);
            }
            if (chantier.photos_after) {
                const urls = chantier.photos_after.map(f => pb.files.getURL(chantier, f));
                setPreviewAfter(urls);
            }
            if (chantier.plans) {
                const urls = chantier.plans.map(f => ({ name: f, url: pb.files.getURL(chantier, f) }));
                setPreviewPlans(urls);
            }
        }
    }, [chantier]);

    const loadCustomers = async () => {
        try {
            const records = await pb.collection('customers').getFullList({ sort: 'name' });
            setCustomers(records);
        } catch (err) {
            toast.error("Erreur de chargement des clients");
        }
    };

    // Geocode address for mini-map and save lat/lon if missing
    useEffect(() => {
        const geocode = async () => {
            if (!formData.address || formData.address.length < 5) {
                setShowMap(false);
                return;
            }
            // If we already have lat/lon from autocomplete, just use it
            if (formData.lat && formData.lon) {
                setMapCenter([formData.lat, formData.lon]);
                setShowMap(true);
                return;
            }
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.address)}&limit=1`);
                const data = await response.json();
                if (data && data.length > 0) {
                    const parsedLat = parseFloat(data[0].lat);
                    const parsedLon = parseFloat(data[0].lon);
                    setMapCenter([parsedLat, parsedLon]);
                    setShowMap(true);
                    setFormData(prev => ({ ...prev, lat: parsedLat, lon: parsedLon }));
                }
            } catch (err) {
                console.error("Geocoding failed for mini-map", err);
            }
        };

        const timer = setTimeout(geocode, 1000);
        return () => clearTimeout(timer);
    }, [formData.address, formData.lat, formData.lon]);

    const calculatePaidPercentage = () => {
        if (!formData.budget_total || formData.budget_total <= 0) return 0;
        const total = parseFloat(formData.budget_total);
        const paid = parseFloat(formData.budget_paid || 0);
        return Math.min(100, Math.round((paid / total) * 100));
    };

    const handleFileChanges = async (e, type) => {
        const selectedFiles = Array.from(e.target.files);
        if (type === 'before') {
            setFilesBefore(prev => [...prev, ...selectedFiles]);
            const newPreviews = selectedFiles.map(file => URL.createObjectURL(file));
            setPreviewBefore(prev => [...prev, ...newPreviews]);
        } else if (type === 'after') {
            setFilesAfter(prev => [...prev, ...selectedFiles]);
            const newPreviews = selectedFiles.map(file => URL.createObjectURL(file));
            setPreviewAfter(prev => [...prev, ...newPreviews]);
        } else if (type === 'plans') {
            setIsConvertingPdf(true);
            const finalFiles = [];
            
            for (let file of selectedFiles) {
                if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
                    try {
                        const pdfData = await file.arrayBuffer();
                        const loadingTask = pdfjsLib.getDocument({ data: pdfData });
                        const pdf = await loadingTask.promise;
                        
                        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                            const page = await pdf.getPage(pageNum);
                            const viewport = page.getViewport({ scale: 1.5 });
                            const canvas = document.createElement('canvas');
                            const context = canvas.getContext('2d');
                            canvas.height = viewport.height;
                            canvas.width = viewport.width;
                            
                            await page.render({ canvasContext: context, viewport: viewport }).promise;
                            
                            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', 0.8));
                            const imageFile = new File([blob], `${file.name.replace(/\.[^/.]+$/, "")}_page_${pageNum}.webp`, { type: 'image/webp' });
                            finalFiles.push(imageFile);
                        }
                        toast.success(`PDF ${file.name} converti (${pdf.numPages} pages)`);
                    } catch (err) {
                        console.error("Erreur PDF:", err);
                        toast.error("Erreur de conversion PDF : " + file.name);
                        finalFiles.push(file); // Fallback to original
                    }
                } else {
                    finalFiles.push(file);
                }
            }
            
            setFilesPlans(prev => [...prev, ...finalFiles]);
            const newPreviews = finalFiles.map(file => ({ name: file.name, url: URL.createObjectURL(file) }));
            setPreviewPlans(prev => [...prev, ...newPreviews]);
            setIsConvertingPdf(false);
        }
    };

    const removeFile = (index, type) => {
        if (type === 'before') {
            setFilesBefore(prev => prev.filter((_, i) => i !== index));
            setPreviewBefore(prev => prev.filter((_, i) => i !== index));
        } else if (type === 'after') {
            setFilesAfter(prev => prev.filter((_, i) => i !== index));
            setPreviewAfter(prev => prev.filter((_, i) => i !== index));
        } else if (type === 'plans') {
            setFilesPlans(prev => prev.filter((_, i) => i !== index));
            setPreviewPlans(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleUserSelect = (userId, field) => {
        if (!userId) return;
        setFormData(prev => {
            if (prev[field].includes(userId)) return prev;
            return {
                ...prev,
                [field]: [...prev[field], userId]
            };
        });
    };

    const removeUser = (userId, field) => {
        setFormData(prev => ({
            ...prev,
            [field]: prev[field].filter(id => id !== userId)
        }));
    };

    const getUserDetails = (userId) => {
        return allUsers.find(u => u.id === userId);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!formData.name.trim()) errors.name = "Nom requis";
        if (!formData.client) errors.client = "Client requis";

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsSubmitting(true);
        try {
            const data = new FormData();

            // Append all standard fields
            Object.keys(formData).forEach(key => {
                const value = formData[key];

                // Special handling for array fields (chef_chantier, workers)
                if (Array.isArray(value)) {
                    // PocketBase expects multiple values for relation fields to be appended repeatedly
                    value.forEach(v => data.append(key, v));
                } else if (value !== '' && value !== null) {
                    data.append(key, value);
                }
            });

            // Append files
            filesBefore.forEach(file => data.append('photos_before', file));
            filesAfter.forEach(file => data.append('photos_after', file));
            filesPlans.forEach(file => data.append('plans', file));

            // Add a client token if missing for the magic link
            if (!chantier && !formData.client_token) {
                data.append('client_token', Math.random().toString(36).substring(2, 15));
            }

            let record;
            if (chantier) {
                record = await pb.collection('chantiers').update(chantier.id, data);
                toast.success("Chantier mis à jour");
            } else {
                record = await pb.collection('chantiers').create(data);
                toast.success("Nouveau chantier créé");
            }
            onSave(record);
        } catch (err) {
            console.error(err);
            toast.error("Erreur de sauvegarde : " + err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const mainPortal = createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 15 }}>
            <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 850, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    
                    <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--color-light-background)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                                <FontAwesomeIcon icon={chantier ? faEdit : faPlus} size="lg" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: 900, color: 'var(--color-text)' }}>{chantier ? 'Modifier le Chantier' : 'Nouveau Chantier'}</h3>
                                <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-light)', fontWeight: 600 }}>Suivi technique et financier</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                            <span style={{
                                background: 'var(--color-light-background)',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '0.75rem',
                                fontWeight: '800',
                                color: 'var(--color-text)'
                            }}>
                                {formData.status === 'PLANNING' ? '🗓️ PLANIFIÉ' :
                                    formData.status === 'EN_COURS' ? '🚀 EN COURS' :
                                        formData.status === 'TERMINE' ? '✅ TERMINÉ' : '❌ ANNULÉ'}
                            </span>
                            <button type="button" onClick={onCancel} style={{ background: 'var(--color-light-background)', border: 'none', cursor: 'pointer', color: 'var(--color-text-light)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaTimes />
                            </button>
                        </div>
                    </div>

                <div className={styles.tabsContainer} style={{ padding: '0 30px', background: 'var(--color-light-background)', borderBottom: '1px solid var(--color-light-gray)', flexShrink: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0, margin: 0 }}>
                    <button type="button" className={`${styles.tabLink} ${activeTab === 'general' ? styles.tabLinkActive : ''}`} onClick={() => setActiveTab('general')}>
                        <FontAwesomeIcon icon={faInfoCircle} /> Informations
                    </button>
                    <button type="button" className={`${styles.tabLink} ${activeTab === 'team' ? styles.tabLinkActive : ''}`} onClick={() => setActiveTab('team')}>
                        <FontAwesomeIcon icon={faUsers} /> Équipe
                    </button>
                    <button type="button" className={`${styles.tabLink} ${activeTab === 'finance' ? styles.tabLinkActive : ''}`} onClick={() => setActiveTab('finance')}>
                        <FontAwesomeIcon icon={faEuroSign} /> Finances
                    </button>
                    <button type="button" className={`${styles.tabLink} ${activeTab === 'media' ? styles.tabLinkActive : ''}`} onClick={() => setActiveTab('media')}>
                        <FontAwesomeIcon icon={faImages} /> Photos
                    </button>
                    <button type="button" className={`${styles.tabLink} ${activeTab === 'docs' ? styles.tabLinkActive : ''}`} onClick={() => setActiveTab('docs')}>
                        <FontAwesomeIcon icon={faFilePdf} /> Documents
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    <div style={{ padding: '25px 30px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 }}>
                    {activeTab === 'general' && (
                        <div className={styles.formGrid}>
                            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                <label>Nom du Chantier *</label>
                                <input name="name" value={formData.name} onChange={handleChange} placeholder="Ex: Rénovation Cuisine" />
                                {formErrors.name && <div className={styles.fieldError}>{formErrors.name}</div>}
                            </div>

                            <div className={styles.formGroup}>
                                <label>Client *</label>
                                <div className={styles.selectWrapper}>
                                    <select name="client" value={formData.client} onChange={handleChange} style={{ flex: 1 }}>
                                        <option value="">Choisir...</option>
                                        {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                    <button
                                        type="button"
                                        className={styles.addQuickBtn}
                                        onClick={() => setShowQuickAddClient(true)}
                                        title="Ajouter un nouveau client"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                                {formErrors.client && <div className={styles.fieldError}>{formErrors.client}</div>}
                            </div>

                            <div className={styles.formGroup}>
                                <label>Libellé Équipe (optionnel)</label>
                                <input name="team" value={formData.team} onChange={handleChange} placeholder="Ex: Équipe A, Menuisiers..." />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Statut Actuel</label>
                                <select name="status" value={formData.status} onChange={handleChange}>
                                    <option value="PLANNING">Planning</option>
                                    <option value="EN_COURS">En Cours</option>
                                    <option value="TERMINE">Terminé</option>
                                    <option value="ANNULE">Annulé</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Progression Globale ({formData.progress}%)</label>
                                <div className={styles.rangeWrapper}>
                                    <input className={styles.rangeInput} type="range" name="progress" min="0" max="100" value={formData.progress} onChange={handleChange} />
                                    <span style={{ fontWeight: '800', width: '40px', textAlign: 'right' }}>{formData.progress}%</span>
                                </div>
                            </div>

                            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                <label><FontAwesomeIcon icon={faMapMarkedAlt} /> Adresse Exacte</label>
                                <AddressAutocomplete value={formData.address} onChange={(v, coords) => {
                                    if (coords) {
                                        setFormData({ ...formData, address: v, lat: coords.lat, lon: coords.lon });
                                    } else {
                                        setFormData({ ...formData, address: v, lat: null, lon: null });
                                    }
                                }} />
                            </div>

                            {showMap && (
                                <div className={styles.miniMapContainer}>
                                    <MapContainer center={mapCenter} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                        <Marker position={mapCenter} />
                                    </MapContainer>
                                </div>
                            )}

                            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                <label>Description / Description du projet</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Détails, codes d'accès, consignes particulières..." style={{ minHeight: '100px' }} />
                            </div>
                        </div>
                    )}

                    {activeTab === 'team' && (
                        <div className={styles.formGrid}>
                            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                <label>Chefs de Chantier (Encadrement)</label>
                                <div className={styles.userSelector}>
                                    <div className={styles.selectedUsersList}>
                                        {formData.chef_chantier.map(id => {
                                            const user = getUserDetails(id);
                                            return user ? (
                                                <div key={id} className={styles.userBadge}>
                                                    {user.avatar ? (
                                                        <img src={pb.files.getURL(user, user.avatar)} alt="" />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faUserTie} style={{ color: 'var(--color-primary)' }} />
                                                    )}
                                                    <span>{user.name || user.username}</span>
                                                    <button type="button" onClick={() => removeUser(id, 'chef_chantier')} className={styles.removeUserBtn}>×</button>
                                                </div>
                                            ) : null;
                                        })}
                                        {formData.chef_chantier.length === 0 && (
                                            <span style={{ fontSize: '0.8rem', opacity: 0.5, fontStyle: 'italic' }}>Aucun chef assigné</span>
                                        )}
                                    </div>
                                    <select
                                        className={styles.userDropdown}
                                        value=""
                                        onChange={(e) => handleUserSelect(e.target.value, 'chef_chantier')}
                                    >
                                        <option value="">+ Ajouter un chef...</option>
                                        {allUsers.filter(u => u.role === 'foreman' || u.role === 'admin' || u.role === 'employee').map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.name || u.username} ({u.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <label style={{ margin: 0 }}>Budget Main d'œuvre (Total Heures)</label>
                                    <div style={{ position: 'relative', width: '120px' }}>
                                        <input type="number" name="budget_hours" value={formData.budget_hours} onChange={handleChange} placeholder="0" style={{ paddingRight: '30px' }} />
                                        <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', fontWeight: '800', color: '#94a3b8' }}>H</span>
                                    </div>
                                </div>
                                
                                {formData.budget_hours > 0 && (
                                    <div style={{ background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '16px', padding: '15px', marginBottom: '20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '900', color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>
                                            <span>Consommation Main d'œuvre (Réel)</span>
                                            <span>Budget: {formData.budget_hours}h</span>
                                        </div>
                                        <div className={styles.progressBarTrack} style={{ height: '10px' }}>
                                            <div className={styles.progressBarFill} style={{ 
                                                width: `${Math.min(100, (chantier?.real_hours || 0) / formData.budget_hours * 100)}%`,
                                                backgroundColor: (chantier?.real_hours || 0) > formData.budget_hours ? '#ef4444' : '#10b981'
                                            }} />
                                        </div>
                                        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '700' }}>
                                            <span style={{ color: (chantier?.real_hours || 0) > formData.budget_hours ? '#ef4444' : '#1e293b' }}>
                                                {chantier?.real_hours || 0}h effectuées (dont {chantier?.approved_hours || 0}h validées)
                                            </span>
                                            <span style={{ color: '#64748b' }}>
                                                Prévu planning : {chantier?.planned_hours || 0}h
                                            </span>
                                        </div>
                                    </div>
                                )}
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '10px' }}>
                                    <label style={{ margin: 0 }}>Ouvriers assignés (Effectif)</label>
                                    <div style={{ display: 'flex', gap: '8px', fontSize: '10px', fontWeight: '800' }}>
                                        <span style={{ background: '#f8fafc', padding: '4px 8px', borderRadius: '6px', color: '#64748b', border: '1px solid #e2e8f0' }}>
                                            {chantier?.unique_workers_planned || 0} planifié(s)
                                        </span>
                                        <span style={{ background: (chantier?.unique_workers_real || 0) > 0 ? '#f0fdf4' : '#f8fafc', padding: '4px 8px', borderRadius: '6px', color: (chantier?.unique_workers_real || 0) > 0 ? '#166534' : '#64748b', border: '1px solid ' + ((chantier?.unique_workers_real || 0) > 0 ? '#dcfce7' : '#e2e8f0') }}>
                                            {chantier?.unique_workers_real || 0} intervenu(s)
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.userSelector}>
                                    <div className={styles.selectedUsersList}>
                                        {formData.workers.map(id => {
                                            const user = getUserDetails(id);
                                            return user ? (
                                                <div key={id} className={styles.userBadge}>
                                                    {user.avatar ? (
                                                        <img src={pb.files.getURL(user, user.avatar)} alt="" />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faTasks} style={{ color: '#64748b' }} />
                                                    )}
                                                    <span>{user.name || user.username}</span>
                                                    <button type="button" onClick={() => removeUser(id, 'workers')} className={styles.removeUserBtn}>×</button>
                                                </div>
                                            ) : null;
                                        })}
                                        {formData.workers.length === 0 && (
                                            <span style={{ fontSize: '0.8rem', opacity: 0.5, fontStyle: 'italic' }}>Aucun ouvrier assigné</span>
                                        )}
                                    </div>
                                    <select
                                        className={styles.userDropdown}
                                        value=""
                                        onChange={(e) => handleUserSelect(e.target.value, 'workers')}
                                    >
                                        <option value="">+ Ajouter un ouvrier...</option>
                                        {allUsers.filter(u => u.role === 'worker' || u.role === 'employee' || u.role === 'foreman').map(u => (
                                            <option key={u.id} value={u.id}>
                                                {u.name || u.username} ({u.role})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'finance' && (
                        <div className={styles.formGrid}>
                            <div className={styles.formGroup}>
                                <label>Budget Total HT (€)</label>
                                <div style={{ position: 'relative' }}>
                                    <input type="number" name="budget_total" value={formData.budget_total} onChange={handleChange} placeholder="0.00" style={{ paddingLeft: '35px' }} />
                                    <FontAwesomeIcon icon={faEuroSign} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                                </div>
                            </div>
                            <div className={styles.formGroup}>
                                <label>Acompte versé (€)</label>
                                <div style={{ position: 'relative' }}>
                                    <input type="number" name="budget_deposit" value={formData.budget_deposit} onChange={handleChange} placeholder="0.00" style={{ paddingLeft: '35px' }} />
                                    <FontAwesomeIcon icon={faEuroSign} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                                </div>
                            </div>
                            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                <label>Montant déjà payé (€)</label>
                                <div style={{ position: 'relative' }}>
                                    <input type="number" name="budget_paid" value={formData.budget_paid} onChange={handleChange} placeholder="0.00" style={{ paddingLeft: '35px' }} />
                                    <FontAwesomeIcon icon={faEuroSign} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} />
                                </div>
                            </div>

                            <div className={styles.financeProgressContainer}>
                                <div className={styles.financeHeader}>
                                    <span className={styles.financeTitle}>Santé Financière / Encaissement</span>
                                    <span className={styles.financeValue}>{calculatePaidPercentage()}% payé</span>
                                </div>
                                <div className={styles.progressBarTrack}>
                                    <div className={styles.progressBarFill} style={{ width: `${calculatePaidPercentage()}%` }} />
                                </div>
                                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: '700', color: '#64748b' }}>
                                    <span>Reste à percevoir: {((formData.budget_total || 0) - (formData.budget_paid || 0)).toLocaleString()} €</span>
                                    <span>Total: {Number(formData.budget_total || 0).toLocaleString()} €</span>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label>Date de début</label>
                                <input type="date" name="start_date" value={formData.start_date} onChange={handleChange} />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Fin estimée</label>
                                <input type="date" name="end_date_estimated" value={formData.end_date_estimated} onChange={handleChange} />
                            </div>
                        </div>
                    )}

                    {activeTab === 'media' && (
                        <div className={styles.formGrid}>
                            <div className={styles.galleryGroup}>
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '0.8rem', fontWeight: '800', color: '#64748b' }}>
                                        <FontAwesomeIcon icon={faImages} /> PHOTOS AVANT
                                    </label>
                                    <div className={styles.dropzone} onClick={() => beforeInputRef.current.click()}>
                                        <FontAwesomeIcon icon={faPlus} size="lg" style={{ color: 'var(--color-primary)', marginBottom: '8px' }} />
                                        <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '700' }}>Ajouter des photos</p>
                                    </div>
                                    <input type="file" ref={beforeInputRef} multiple hidden onChange={(e) => handleFileChanges(e, 'before')} accept="image/*" />

                                    <div className={styles.previewImageGroup}>
                                        {previewBefore.map((src, i) => (
                                            <div key={i} className={styles.previewCard}>
                                                <img src={src} alt="" />
                                                <button type="button" className={styles.removeFileBtn} onClick={() => removeFile(i, 'before')}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '0.8rem', fontWeight: '800', color: 'var(--color-primary)' }}>
                                        <FontAwesomeIcon icon={faImages} /> PHOTOS APRÈS
                                    </label>
                                    <div className={styles.dropzone} onClick={() => afterInputRef.current.click()}>
                                        <FontAwesomeIcon icon={faPlus} size="lg" style={{ color: 'var(--color-primary)', marginBottom: '8px' }} />
                                        <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: '700' }}>Ajouter des photos</p>
                                    </div>
                                    <input type="file" ref={afterInputRef} multiple hidden onChange={(e) => handleFileChanges(e, 'after')} accept="image/*" />

                                    <div className={styles.previewImageGroup}>
                                        {previewAfter.map((src, i) => (
                                            <div key={i} className={styles.previewCard}>
                                                <img src={src} alt="" />
                                                <button type="button" className={styles.removeFileBtn} onClick={() => removeFile(i, 'after')}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'docs' && (
                        <div className={styles.formGrid}>
                            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', fontSize: '0.8rem', fontWeight: '800', color: '#64748b' }}>
                                    <FontAwesomeIcon icon={faFolderOpen} /> DOSSIER TECHNIQUE
                                </label>
                                <div className={styles.dropzone} onClick={() => !isConvertingPdf && plansInputRef.current.click()} style={{ opacity: isConvertingPdf ? 0.5 : 1, cursor: isConvertingPdf ? 'not-allowed' : 'pointer' }}>
                                    <FontAwesomeIcon icon={faFilePdf} style={{ color: '#94a3b8', marginBottom: '10px' }} size="2x" />
                                    <p style={{ margin: 0, fontWeight: '700' }}>
                                        {isConvertingPdf ? '⏳ Conversion des PDF en cours, veuillez patienter...' : 'Cliquer pour ajouter des plans PDF, schémas ou images'}
                                    </p>
                                </div>
                                <input type="file" ref={plansInputRef} multiple hidden onChange={(e) => handleFileChanges(e, 'plans')} accept=".pdf,.dwg,.jpg,.png,.webp" />

                                <div style={{ marginTop: '20px', display: 'grid', gap: '10px' }}>
                                    {previewPlans.map((file, i) => (
                                        <div key={i} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '12px 16px',
                                            background: '#f8fafc',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ background: '#fee2e2', padding: '8px', borderRadius: '8px', color: '#ef4444' }}>
                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                </div>
                                                <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>{file.name}</span>
                                            </div>
                                            <button type="button" onClick={() => removeFile(i, 'plans')} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '5px' }}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {chantier && (
                                <div className={styles.formGroupFull}>
                                    <div className={styles.sectionLegend}>Accès Client (Lien Magique)</div>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#f8fafc', padding: '15px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ flex: 1, fontSize: '0.8rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                            {window.location.origin}/access/${chantier.id}/${chantier.client_token}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(`${window.location.origin}/access/${chantier.id}/${chantier.client_token}`);
                                                toast.success("Lien copié !");
                                            }}
                                            className={styles.submitBtn}
                                            style={{ padding: '8px 16px', fontSize: '0.8rem', borderRadius: '10px' }}
                                        >
                                            <FontAwesomeIcon icon={faLink} /> Copier
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    </div>
                    <div style={{ padding: '20px 30px', background: 'var(--color-light-background)', borderTop: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div />
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button type="button" onClick={onCancel} disabled={isSubmitting} style={{ padding: '10px 24px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                                Annuler
                            </button>
                            <button type="submit" disabled={isSubmitting} style={{ padding: '10px 28px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.2)' }}>
                                <FontAwesomeIcon icon={faCheckCircle} /> {isSubmitting ? 'Chargement...' : (chantier ? 'Enregistrer les modifications' : 'Lancer le chantier')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        </div>,
        document.body
    );

    const quickAddPortal = showQuickAddClient && createPortal(
        <div className={styles.glassOverlay}>
            <div className={styles.glassModal}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Nouveau Client Rapide</h3>
                    <button onClick={() => setShowQuickAddClient(false)} className={styles.closeButton} style={{ position: 'static' }}>×</button>
                </div>

                <form onSubmit={handleQuickClientSubmit}>
                    <div className={styles.formGroup}>
                        <label>Nom / Société *</label>
                        <input
                            value={quickClientData.name}
                            onChange={e => setQuickClientData({ ...quickClientData, name: e.target.value })}
                            placeholder="M. Dupont ou SAS Immobilier"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Email *</label>
                        <input
                            type="email"
                            value={quickClientData.email}
                            onChange={e => setQuickClientData({ ...quickClientData, email: e.target.value })}
                            placeholder="client@email.com"
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Téléphone</label>
                        <input
                            value={quickClientData.phone}
                            onChange={e => setQuickClientData({ ...quickClientData, phone: e.target.value })}
                            placeholder="06 00 00 00 00"
                        />
                    </div>

                    <div className={styles.formGroup} style={{ marginTop: '20px' }}>
                        <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                            Accès Client (Mot de passe)
                            <span
                                onClick={generatePassword}
                                style={{ color: 'var(--color-primary)', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' }}
                            >
                                Générer un code
                            </span>
                        </label>
                        {quickClientData.password && (
                            <div className={styles.passwordDisplay}>
                                {quickClientData.password}
                                <span
                                    className={styles.copyBadge}
                                    onClick={() => {
                                        navigator.clipboard.writeText(quickClientData.password);
                                        toast.success("Mot de passe copié !");
                                    }}
                                >
                                    COPIER
                                </span>
                            </div>
                        )}
                        <input
                            type="text"
                            value={quickClientData.password}
                            onChange={e => setQuickClientData({ ...quickClientData, password: e.target.value })}
                            placeholder="Mot de passe personnalisé..."
                            style={{ fontSize: '0.85rem' }}
                        />
                    </div>

                    <div style={{ marginTop: '2.5rem', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <button type="submit" className={styles.submitBtn} disabled={isCreatingClient}>
                            {isCreatingClient ? 'Création...' : 'Créer et Sélectionner'}
                        </button>
                        <p style={{ fontSize: '0.7rem', color: '#64748b', textAlign: 'center', margin: 0 }}>
                            Note: Le mail ne sera pas envoyé automatiquement.
                            <br />Pensez à transmettre les identifiants manuellement.
                        </p>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );

    return (
        <>
            {mainPortal}
            {quickAddPortal}
        </>
    );
}

export default ChantierForm;
